type RecordValue = Record<string, unknown>;

type RequirementValidationDependencies = Readonly<{
  valueTypes: readonly string[];
  units: readonly string[];
  isRecord: (value: unknown) => value is RecordValue;
  hasOwn: (record: RecordValue, key: string) => boolean;
  isOneOf: (value: string, values: readonly string[]) => boolean;
  requireString: (record: RecordValue, key: string, errors: string[], errorKey: string) => void;
  requireEnum: (record: RecordValue, key: string, values: readonly string[], errors: string[], errorKey: string) => void;
  requireNonEmptyStringArray: (record: RecordValue, key: string, errors: string[], errorKey: string) => void;
  requireStringArray: (record: RecordValue, key: string, errors: string[], errorKey: string) => void;
}>;

export function validateCapabilityRequirements(input: Readonly<{
  value: unknown;
  errors: string[];
  errorPath: string;
  dependencies: RequirementValidationDependencies;
}>): void {
  const { value, errors, errorPath, dependencies } = input;
  if (!Array.isArray(value)) {
    errors.push(`${errorPath}_required`);
    return;
  }
  value.forEach((entry, index) => validateRequirement({
    requirement: dependencies.isRecord(entry) ? entry : null,
    errors,
    requirementPath: `${errorPath}[${index}]`,
    dependencies,
  }));
}

function validateRequirement(input: Readonly<{
  requirement: RecordValue | null;
  errors: string[];
  requirementPath: string;
  dependencies: RequirementValidationDependencies;
}>): void {
  const { requirement, errors, requirementPath, dependencies } = input;
  if (!requirement) {
    errors.push(`${requirementPath}_object_required`);
    return;
  }
  dependencies.requireString(requirement, 'requirementId', errors, `${requirementPath}.requirementId`);
  dependencies.requireString(requirement, 'label', errors, `${requirementPath}.label`);
  if (typeof requirement.required !== 'boolean') errors.push(`${requirementPath}.required_required`);
  validateRequirementValueType(requirement, errors, requirementPath, dependencies);
  validateRequirementAllowedValues(requirement, errors, requirementPath, dependencies);
  validateRequirementUnit(requirement, errors, requirementPath, dependencies);
  validateModelInterpretedScenarioNumeric(requirement, errors, requirementPath, dependencies);
  if (dependencies.hasOwn(requirement, 'requiredEvidenceKeysWhenPresent')) {
    dependencies.requireStringArray(requirement, 'requiredEvidenceKeysWhenPresent', errors, `${requirementPath}.requiredEvidenceKeysWhenPresent`);
  }
  validateRequirementDefault(requirement, errors, requirementPath, dependencies);
}

function validateRequirementValueType(requirement: RecordValue, errors: string[], requirementPath: string, dependencies: RequirementValidationDependencies): void {
  if (!dependencies.hasOwn(requirement, 'valueType')) return;
  dependencies.requireEnum(requirement, 'valueType', dependencies.valueTypes, errors, `${requirementPath}.valueType`);
}

function validateRequirementAllowedValues(requirement: RecordValue, errors: string[], requirementPath: string, dependencies: RequirementValidationDependencies): void {
  if (!dependencies.hasOwn(requirement, 'allowedValues')) return;
  dependencies.requireNonEmptyStringArray(requirement, 'allowedValues', errors, `${requirementPath}.allowedValues`);
  if (requirement.valueType !== 'string') errors.push(`${requirementPath}.allowedValues_string_required`);
  if (Array.isArray(requirement.allowedValues) && new Set(requirement.allowedValues).size !== requirement.allowedValues.length) {
    errors.push(`${requirementPath}.allowedValues_duplicate`);
  }
}

function validateRequirementUnit(requirement: RecordValue, errors: string[], requirementPath: string, dependencies: RequirementValidationDependencies): void {
  if (!dependencies.hasOwn(requirement, 'unit')) return;
  dependencies.requireEnum(requirement, 'unit', dependencies.units, errors, `${requirementPath}.unit`);
}

function validateModelInterpretedScenarioNumeric(requirement: RecordValue, errors: string[], requirementPath: string, dependencies: RequirementValidationDependencies): void {
  if (!dependencies.hasOwn(requirement, 'modelInterpretedScenarioNumeric')) return;
  const bounds = dependencies.isRecord(requirement.modelInterpretedScenarioNumeric)
    ? requirement.modelInterpretedScenarioNumeric
    : null;
  if (!bounds) {
    errors.push(`${requirementPath}.modelInterpretedScenarioNumeric_object_required`);
    return;
  }
  if (
    requirement.valueType !== 'number' ||
    typeof requirement.unit !== 'string' ||
    !dependencies.isOneOf(requirement.unit, dependencies.units)
  ) {
    errors.push(`${requirementPath}.modelInterpretedScenarioNumeric_number_unit_required`);
  }
  const minimum = bounds.minimum;
  const maximum = bounds.maximum;
  if (typeof minimum !== 'number' || !Number.isFinite(minimum)
    || typeof maximum !== 'number' || !Number.isFinite(maximum) || minimum >= maximum) {
    errors.push(`${requirementPath}.modelInterpretedScenarioNumeric_bounds_invalid`);
  }
}

function validateRequirementDefault(requirement: RecordValue, errors: string[], requirementPath: string, dependencies: RequirementValidationDependencies): void {
  if (!dependencies.hasOwn(requirement, 'defaultWhenAbsent')) return;
  const defaultSpec = dependencies.isRecord(requirement.defaultWhenAbsent) ? requirement.defaultWhenAbsent : null;
  if (!defaultSpec) {
    errors.push(`${requirementPath}.defaultWhenAbsent_object_required`);
    return;
  }
  const valueType = typeof defaultSpec.value;
  if (valueType !== 'boolean' && valueType !== 'number' && valueType !== 'string') {
    errors.push(`${requirementPath}.defaultWhenAbsent.value_invalid`);
  }
  const declaredValueType = typeof requirement.valueType === 'string'
    && dependencies.isOneOf(requirement.valueType, dependencies.valueTypes)
    ? requirement.valueType
    : null;
  if (declaredValueType && valueType !== declaredValueType) {
    errors.push(`${requirementPath}.defaultWhenAbsent.valueType_mismatch`);
  }
  if (Array.isArray(requirement.allowedValues) && typeof defaultSpec.value === 'string' && !requirement.allowedValues.includes(defaultSpec.value)) {
    errors.push(`${requirementPath}.defaultWhenAbsent.value_not_allowed`);
  }
  const disclosure = typeof defaultSpec.disclosure === 'string' ? defaultSpec.disclosure.trim() : '';
  if (!disclosure || /\d/.test(disclosure)) {
    errors.push(`${requirementPath}.defaultWhenAbsent.disclosure_invalid`);
  }
}
